import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { TaskState } from 'api-client';
 
interface TaskSummaryProps {
  tasks: TaskState[];
}
 
export const TaskSummaryReport = ({ tasks }: TaskSummaryProps) => {
  // Grouping Logic
  const getGroup = (status: string | undefined | null) => {
    const s = status?.toLowerCase();
    if (s === 'completed') return 'success';
    if (['failed', 'error'].includes(s || '')) return 'failed';
    if (['skipped', 'canceled', 'killed'].includes(s || '')) return 'skipped';
    return 'others';
  };
 
  const total = tasks.length;
  const successful = tasks.filter(t => getGroup(t.status) === 'success').length;
  const failed = tasks.filter(t => getGroup(t.status) === 'failed').length;
  const skipped = tasks.filter(t => getGroup(t.status) === 'skipped').length;
  const others = tasks.filter(t => getGroup(t.status) === 'others').length;
 
  const getPct = (val: number) => (total > 0 ? (val / total) * 100 : 0);
  const successPercentateRate = total > 0 ? Math.round((successful / total) * 100) : 0;
  const categories = Array.from(new Set(tasks.map(t => t.category || 'Unknown')));
  const categoryStats = categories.map(cat => {
    const catTasks = tasks.filter(t => t.category === cat);
    return {
      name: cat,
      success: catTasks.filter(t => getGroup(t.status) === 'success').length,
      failed: catTasks.filter(t => getGroup(t.status) === 'failed').length,
      skipped: catTasks.filter(t => getGroup(t.status) === 'skipped').length,
      others: catTasks.filter(t => getGroup(t.status) === 'others').length,
      total: catTasks.length,
    };
  });
 
  return (
    <div style={{ flexGrow: 1, marginTop: '16px', padding: '10px' }}>
      {/* 1. Summary Cards (Now including Total Tasks) */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
        {[
          { label: 'TOTAL TASKS', value: total, color: '#212121', border: '#e0e0e0' },
          { label: 'SUCCESSFUL', value: successful, color: '#4caf50', border: '#4caf50' },
          { label: 'FAILED', value: failed, color: '#f44336', border: '#f44336' },
          { label: 'SKIPPED', value: skipped, color: '#ff9800', border: '#ff9800' },
          { label: 'OTHERS', value: others, color: '#2196f3', border: '#2196f3' }
        ].map((item, idx) => (
          <div key={idx} style={{
            flex: '1 1 130px',
            padding: '16px',
            border: '1px solid #e0e0e0',
            borderTop: `4px solid ${item.border}`,
            borderRadius: '8px',
            backgroundColor: 'white'
          }}>
            <Typography color="textSecondary" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{item.label}</Typography>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: item.color }}>{item.value}</Typography>
          </div>
        ))}
      </div>
 
      {/* 2. Task Distribution Bar */}
      <div style={{ marginBottom: '40px' }}>
        <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>Task Distribution (Success Rate: {successPercentateRate}%)</Typography>
        <div style={{ width: '100%', height: '15px', display: 'flex', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#e0e0e0' }}>
          <div style={{ width: `${getPct(successful)}%`, backgroundColor: '#4caf50' }} title={`Success: ${successful}`}/>
          <div style={{ width: `${getPct(failed)}%`, backgroundColor: '#f44336' }} title={`Failed: ${failed}`} />
          <div style={{ width: `${getPct(skipped)}%`, backgroundColor: '#ff9800' }} title={`Skipped: ${skipped}`} />
          <div style={{ width: `${getPct(others)}%`, backgroundColor: '#2196f3' }} title={`Others: ${others}`} />
        </div>
 
        {/* Fixed Legend: Replaced Box with div */}
        <div style={{ display: 'flex', gap: '24px' }}>
          {[
            { label: 'Success', color: '#4caf50' },
            { label: 'Failed', color: '#f44336' },
            { label: 'Skipped', color: '#ff9800' },
            { label: 'Others', color: '#2196f3' }
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: l.color }} />
              <Typography variant="caption" color="textSecondary">{l.label}</Typography>
            </div>
          ))}
        </div>
      </div>
 
      {/* 3. Category Table */}
      <TableContainer component={Paper} variant="outlined" style={{ marginBottom: '32px' }}>
        <Table size="small">
          <TableHead style={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>CATEGORY</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>SUCCESS</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>FAILED</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>SKIPPED</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>OTHERS</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>TOTAL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categoryStats.map((row) => (
              <TableRow key={row.name}>
                <TableCell style={{ fontWeight: '500' }}>{row.name}</TableCell>
                <TableCell style={{ color: '#4caf50', fontWeight: 'bold' }}>{row.success}</TableCell>
                <TableCell style={{ color: '#f44336', fontWeight: 'bold' }}>{row.failed}</TableCell>
                <TableCell style={{ color: '#ff9800', fontWeight: 'bold' }}>{row.skipped}</TableCell>
                <TableCell style={{ color: '#2196f3', fontWeight: 'bold' }}>{row.others}</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>{row.total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
 
      {/* 4. Status Guide */}
      <div style={{ padding: '16px', border: '1px dashed #bdbdbd', borderRadius: '8px', backgroundColor: '#fafafa' }}>
        <Typography variant="caption" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#616161' }}>
          STATUS GUIDE
        </Typography>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '8px' }}>
          <Typography variant="caption" color="textSecondary">• <b>Success:</b> completed</Typography>
          <Typography variant="caption" color="textSecondary">• <b>Failed:</b> error, failed</Typography>
          <Typography variant="caption" color="textSecondary">• <b>Skipped:</b> skipped, canceled, killed</Typography>
          <Typography variant="caption" color="textSecondary">• <b>Others:</b> queued, uninitialized, blocked, standby, underway, delayed</Typography>
          
        </div>
      </div>
    </div>
  );
};